// Telling us what went wrong, with the board attached.
//
// **The board is what makes a report worth having.** "The belts went wrong" is
// a sentence somebody has to reproduce; the same sentence with the position on
// it is something that can be loaded and looked at. So this sends the state
// through the *same* codec `runSave.ts` writes a save with — one encoding, so a
// report is a board somebody can restore rather than a blob nobody opens twice.
//
// **Never `window.prompt`.** A native dialog is the browser's chrome, it blocks
// the main thread so the board freezes behind it, and Playwright cannot reach
// one — the rule this file exists under rather than an aside.

import { useState } from "react";

import { encode } from "src/engine/game/codec";
import { hashState, hashToHex } from "src/engine/rules/hash";
import type { GameState } from "src/engine/rules/commands";

import {
  Btn,
  Link,
  Modal,
  ModalBody,
  ModalTitle,
  Notice,
  SettingRow,
  styled,
  Text,
  TextArea,
  Toggle
} from "./kit";

/** What the server will take. Mirrors `server/feedback.ts`. */
export const MAX_MESSAGE = 4_000;

/** Where the report ends up, and what came back. */
type Sending = "idle" | "sending" | "sent" | "failed" | "unavailable";

export interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * The board as it stands, or null on the menu.
   *
   * Passed rather than read from a store, so the dialog captures **what the
   * player can see** at the moment they opened it — which is the whole claim
   * the feature makes — instead of whatever the game had moved on to by the
   * time they finished typing.
   */
  state: GameState | null;
  /** A run, a scenario, a room, the menu. Free text, for grouping later. */
  mode: string;
  /** The room they were in, when they were in one. */
  room?: string | null;
  /** Opens the privacy notice, when the host can show one. */
  onPrivacy?: () => void;
  /**
   * The command log, so the report can be *played* and not only looked at.
   *
   * **The snapshot alone cannot be replayed.** `GameState.history` is the chess
   * move list, not the command log — the log lives on the store — so a report
   * carrying only the snapshot is a final position with no way to reach it. The
   * log is the save format, the replay and the network protocol, and this is
   * the fourth thing it is for.
   *
   * Sent *beside* the snapshot rather than instead of it, which is the whole
   * design: a log only means anything against the rules that produced it and
   * this deploys several times a day, so the snapshot is what is always
   * readable and the log is the bonus that usually replays. See
   * `replayReport`.
   */
  log?: readonly unknown[];
}

/**
 * What a report says about the board, in words, before it is sent.
 *
 * **Shown rather than described.** "Includes your game state" is a promise
 * somebody has to take on trust; a ply count and the state hash are the same
 * promise with the receipt attached — and the hash is the identifier that ties
 * the report to a replay if one is ever needed.
 */
function boardLine(state: GameState | null): string {
  if (!state) return "No game open — this will be sent as a note on its own.";
  return `${state.history.length} plies · ${hashToHex(hashState(state))}`;
}

/** A block of the form. Spacing lives here rather than on six call sites. */
const Block = styled("div")(({ theme }) => ({ marginTop: theme.kit.space.md }));

/** The label side of the attach row — two lines, so it is not a bare `span`. */
const AttachLabel = styled("div")({ display: "flex", flexDirection: "column" });

/** The reference the server handed back. Monospace, because it is copied. */
const Reference = styled("div")(({ theme }) => ({
  marginTop: theme.kit.space.xs,
  padding: theme.kit.space.sm,
  background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`,
  border: `2px solid ${theme.kit.color.line}`,
  borderRadius: theme.kit.radius.md,
  fontFamily: theme.kit.font.display,
  fontSize: 12,
  color: theme.kit.color.ink,
  wordBreak: "break-all"
}));

/**
 * The privacy notice's link, which opens a screen rather than a URL.
 *
 * A real button under the link's paint: it is keyboard-reachable for free, and
 * the alternative — a `span` with `role="button"` and a key handler — is three
 * lines re-implementing what the element already does.
 */
const PrivacyLink = styled(Link)({
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  cursor: "pointer",
  textDecoration: "underline"
});

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.kit.space.sm,
  marginTop: theme.kit.space.lg
}));

export interface FeedbackFormProps extends Omit<FeedbackDialogProps, "open" | "onClose"> {
  /** Sent, cancelled or closed — whatever is framing this takes it away. */
  onDone: () => void;
}

/**
 * The form itself, with no frame round it.
 *
 * Split out so the pause menu's Feedback screen is the same form inside the
 * plate (PLAN-UI §28.3) rather than a dialog over a dialog. `FeedbackDialog`
 * below is the frame for everywhere that has no plate.
 */
export function FeedbackForm({
  state,
  mode,
  room,
  log,
  onPrivacy,
  onDone
}: FeedbackFormProps): React.ReactElement {
  /**
   * **The board is captured when the form mounts, not when Send is pressed.**
   * A report is about the position somebody was looking at when something went
   * wrong, and a run that has moved on three turns is a different board. The
   * dialog got this by being mounted only while open; a pause screen has to say
   * it, because the plate around it re-renders with the live game.
   */
  const [snapshot] = useState(() => state);
  const [message, setMessage] = useState("");
  const [attach, setAttach] = useState(true);
  const [sending, setSending] = useState<Sending>("idle");
  /**
   * The reference the server gave this report back.
   *
   * **The only credential an anonymous report can have.** There is no account
   * here, so nothing ties a row to a person — which is good minimisation and
   * makes an erasure request impossible to honour unless the sender is handed
   * something only they hold. Shown once, and not stored: keeping it would be
   * inventing the identifier the design just avoided.
   */
  const [ref, setRef] = useState<string | null>(null);

  const close = (): void => {
    // The text survives a close, so a mis-click is not a lost paragraph. It is
    // cleared only once a report has actually landed.
    setSending("idle");
    onDone();
  };

  const send = (): void => {
    if (message.trim().length === 0 || sending === "sending") return;
    setSending("sending");
    void fetch("/api/feedback", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        message: message.slice(0, MAX_MESSAGE),
        // The same encoding a save uses, so this can be read back by the thing
        // that already knows how to read one.
        state: attach && snapshot ? encode(snapshot) : null,
        hash: attach && snapshot ? hashToHex(hashState(snapshot)) : null,
        build: __BUILD_ID__,
        mode,
        room: room ?? null,
        // Commands are plain data — numbers, strings and small arrays — so
        // they need no encoding pass of their own. Dropped whole when the game
        // is not attached: a log without the board it belongs to is the one
        // combination nothing can use.
        log: attach && snapshot ? (log ?? null) : null
      })
    })
      .then((response) => {
        if (response.ok) {
          void response
            .json()
            .then((body: { ref?: string }) => setRef(body.ref ?? null))
            .catch(() => setRef(null));
          setSending("sent");
          setMessage("");
          return;
        }
        // 503 is "there is nowhere to put this", which is not the player's
        // fault and must not read as though it were. Everything else is a
        // report the sender could fix — too long, or too many.
        setSending(response.status === 503 ? "unavailable" : "failed");
      })
      .catch(() => setSending("failed"));
  };

  const over = message.length > MAX_MESSAGE;

  return (
    <>
      {sending === "sent" ? (
        <>
          <Block>
            <Notice tone="good" data-testid="feedback-sent">
              Thank you — that landed. Send another any time.
            </Notice>
          </Block>
          {ref && (
            <Block>
              <Text tone="dim" size={13} block>
                Your reference, if you ever want this report deleted:
              </Text>
              <Reference data-testid="feedback-ref">{ref}</Reference>
              <Text tone="dim" size={12} block>
                We cannot look this up from your side — it is the only thing
                that identifies your report, so copy it if you want the option.
              </Text>
            </Block>
          )}
        </>
      ) : (
        <>
          <Text tone="dim" size={13} block>
            What happened, what you expected, or what you think of it. This is a
            prototype and every report is read.
          </Text>
          {/*
            **Said before it is sent, not buried in a policy.** What leaves
            the machine is the one thing somebody deserves to know while they
            are deciding whether to type, and a link on its own is a promise
            that the interesting part is elsewhere.
          */}
          <Block>
            <Text tone="dim" size={12} block data-testid="feedback-notice">
              Sent with your message: the build you are on, which mode you were
              in, the board if you leave it ticked, and a coarse network address
              (the last part removed) used only to stop the form being flooded.
              No name, no account, no tracking. Kept for 90 days.{" "}
              {onPrivacy && (
                <PrivacyLink
                  as="button"
                  type="button"
                  data-testid="feedback-privacy"
                  onClick={onPrivacy}
                >
                  What we keep, and how to have it deleted
                </PrivacyLink>
              )}
            </Text>
          </Block>
          <Block>
            <TextArea
              data-testid="feedback-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              // The whole point of the control: a bug report is a paragraph,
              // not a line.
              rows={6}
              autoFocus
              invalid={over}
              placeholder="The belts stopped carrying after I took the rook…"
            />
            <Text tone={over ? "rust" : "dim"} size={12} block>
              {over
                ? `${message.length} characters — the limit is ${MAX_MESSAGE}.`
                : `${message.length} / ${MAX_MESSAGE}`}
            </Text>
          </Block>
          <SettingRow>
            <AttachLabel>
              <Text size={13.5}>Include the current board</Text>
              <Text role="display" tone="dim" size={11} data-testid="feedback-board">
                {boardLine(snapshot)}
              </Text>
            </AttachLabel>
            <Toggle
              data-testid="feedback-attach"
              checked={attach && snapshot !== null}
              disabled={snapshot === null}
              onChange={setAttach}
              aria-label="Include the current board"
            />
          </SettingRow>
          {sending === "failed" && (
            <Block>
              <Notice tone="bad" data-testid="feedback-failed">
                That did not send. It may have been too long, or too many in a
                short time — the text is still here, so it can be tried again.
              </Notice>
            </Block>
          )}
          {sending === "unavailable" && (
            <Block>
              <Notice tone="warn" data-testid="feedback-unavailable">
                Feedback is not being collected on this deployment at the
                moment. Nothing was saved, so this is worth sending again later
                rather than assuming it arrived.
              </Notice>
            </Block>
          )}
        </>
      )}
      <Actions>
        <Btn variant="ghost" data-testid="feedback-cancel" onClick={close}>
          {sending === "sent" ? "Close" : "Cancel"}
        </Btn>
        {sending !== "sent" && (
          <Btn
            variant="primary"
            data-testid="feedback-send"
            onClick={send}
            disabled={message.trim().length === 0 || over || sending === "sending"}
          >
            {sending === "sending" ? "Sending…" : "Send"}
          </Btn>
        )}
      </Actions>
    </>
  );
}

/**
 * The dialog frame. The form is the thing; this is what opens it from anywhere
 * with no plate to sit in.
 */
export function FeedbackDialog({
  open,
  onClose,
  ...form
}: FeedbackDialogProps): React.ReactElement {
  return (
    <Modal open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ModalTitle>Send feedback</ModalTitle>
      <ModalBody>
        {/* Keyed to `open` so a form that was closed and reopened snapshots the
            board again rather than restoring the old one. */}
        <FeedbackForm key={String(open)} {...form} onDone={onClose} />
      </ModalBody>
    </Modal>
  );
}
